import { memo, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import type { PerformanceRow } from '@/api/powerplantDetail';

const PALETTE = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];

interface Props { data: PerformanceRow[]; }

function PerformanceChart({ data }: Props) {
  const { chartData, categories } = useMemo(() => {
    const cats = Array.from(new Set(data.map((r) => r.pp_power_name)));
    const map = new Map<string, Record<string, any>>();
    for (const row of data) {
      if (!map.has(row.pp_log_date)) map.set(row.pp_log_date, { pp_log_date: row.pp_log_date });
      map.get(row.pp_log_date)![row.pp_power_name] = Number(row.pp_power_value);
    }
    return { chartData: Array.from(map.values()), categories: cats };
  }, [data]);

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-[28px] border border-slate-100 bg-white p-6 shadow-[0_20px_60px_-32px_rgba(15,23,42,0.35)] transition-all">
      <h2 className="mb-6 text-[12px] font-black tracking-tight text-slate-900">Performans Eğrisi</h2>

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="pp_log_date" tick={{ fontSize: 11 }} />
          <YAxis width={48} tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ borderRadius: '8px', fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {categories.map((cat, i) => (
            <Line
              key={cat}
              type="stepAfter"
              dataKey={cat}
              stroke={PALETTE[i % PALETTE.length]}
              dot={false}
              strokeWidth={cat === 'Actual' ? 2 : 1.5}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <p className="mt-4 text-sm text-slate-400">Son 24 saat (kW)</p>
    </div>
  );
}

export default memo(PerformanceChart);
